import { LENSES } from './lens'

import type { GisLens } from '../api/types'

export type DeltaTone = 'better' | 'worse' | 'flat'

/**
 * Direction verdict for a period-over-period delta on a lens. `null` (no comparable previous
 * window) or `0` (no change) → 'flat'. The issues lens is inverted (a *drop* in active issues is
 * 'better'), so this routes through `LENSES[lens].invert` — the single source of truth the ranking
 * rows, the coloured `DeltaBadge`, and the hero badge text all share.
 */
export function deltaTone(lens: GisLens, delta: number | null): DeltaTone {
  if (delta === null || delta === 0) {
    return 'flat'
  }
  return (delta > 0) !== LENSES[lens].invert ? 'better' : 'worse'
}

/** Tailwind text-colour class for a tone — shared by every delta surface so colour has one home. */
export function toneClass(tone: DeltaTone): string {
  return tone === 'flat' ? 'text-slate-400' : tone === 'better' ? 'text-emerald-600' : 'text-red-600'
}

/**
 * Plain (un-coloured) badge text for the hero pill, which sits on a coloured gradient with white
 * text — so direction is encoded by the arrow + sign, not colour. The arrow follows the *sign* of
 * the change (▲ for a rise, ▼ for a drop); zero uses a neutral → arrow. The reader knows higher
 * coverage is good and more issues is bad. Percent lenses get a `%` suffix; the issues lens (a raw
 * count) does not.
 */
export function deltaBadgeText(lens: GisLens, delta: number | null): string {
  const unit = lens === 'issues' ? '' : '%'
  if (delta === null) {
    return '— vs last period'
  }
  if (delta === 0) {
    return `→ 0${unit} vs last period`
  }
  const arrow = delta > 0 ? '▲' : '▼'
  const prefix = delta > 0 ? '+' : '' // negative sign already in the number
  return `${arrow} ${prefix}${delta}${unit} vs last period`
}
